import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import {
  ChevronLeft, ChevronRight, Plus, X, ShoppingCart,
  Clock, Users, Lock,
} from 'lucide-react';
import { Button, Modal } from '../components/common';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MEALS = ['breakfast', 'lunch', 'dinner'];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekRange(weekStart) {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, opts)}`;
}

export default function MealPlanPage() {
  const { recipes, mealPlans, updateMealPlan, addGroceryItemsFromRecipes } = useApp();
  const { canAccessMealPlanWeek, promptUpgrade, isPremium } = useSubscription();
  const navigate = useNavigate();

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const ws = getWeekStart(new Date());
    return ws.toISOString().split('T')[0];
  });

  const [showRecipePicker, setShowRecipePicker] = useState(null);
  const [recipeSearch, setRecipeSearch] = useState('');

  const plan = useMemo(() => mealPlans.find((p) => p.weekStart === currentWeekStart), [mealPlans, currentWeekStart]);

  const days = useMemo(() => {
    const empty = {};
    DAYS.forEach((d) => { empty[d] = { breakfast: [], lunch: [], dinner: [] }; });
    return plan?.days ? { ...empty, ...plan.days } : empty;
  }, [plan]);

  const navigateWeek = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + direction * 7);
    const newWeekStart = current.toISOString().split('T')[0];
    if (!canAccessMealPlanWeek(newWeekStart)) {
      promptUpgrade('mealplan_week');
      return;
    }
    setCurrentWeekStart(newWeekStart);
  };

  const isCurrentWeek = useMemo(() => {
    const thisWeek = getWeekStart(new Date()).toISOString().split('T')[0];
    return currentWeekStart === thisWeek;
  }, [currentWeekStart]);

  const addRecipeToDay = useCallback(async (day, meal, recipeId) => {
    const updated = { ...days };
    if (!updated[day]) updated[day] = {};
    if (!updated[day][meal]) updated[day][meal] = [];
    updated[day][meal] = [...updated[day][meal], recipeId];
    await updateMealPlan(currentWeekStart, updated);
    setShowRecipePicker(null);
    setRecipeSearch('');
  }, [days, currentWeekStart, updateMealPlan]);

  const removeRecipeFromDay = useCallback(async (day, meal, recipeId) => {
    const updated = { ...days };
    updated[day][meal] = updated[day][meal].filter((id) => id !== recipeId);
    await updateMealPlan(currentWeekStart, updated);
  }, [days, currentWeekStart, updateMealPlan]);

  const getRecipe = useCallback((id) => recipes.find((r) => r.id === id), [recipes]);

  const allPlannedRecipeIds = useMemo(() => {
    const ids = new Set();
    Object.values(days).forEach((meals) => {
      Object.values(meals).forEach((arr) => {
        if (Array.isArray(arr)) arr.forEach((id) => ids.add(id));
      });
    });
    return [...ids];
  }, [days]);

  const handleGenerateGroceryList = async () => {
    if (allPlannedRecipeIds.length > 0) {
      await addGroceryItemsFromRecipes(allPlannedRecipeIds);
      navigate('/grocery');
    }
  };

  const filteredRecipes = recipeSearch
    ? recipes.filter((r) => r.title.toLowerCase().includes(recipeSearch.toLowerCase()))
    : recipes;

  const today = new Date();
  const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl px-2 py-2">
        <button
          onClick={() => navigateWeek(-1)}
          aria-label="Previous week"
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
        >
          <ChevronLeft size={20} />
          {!isPremium && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-amber-500 text-white flex items-center justify-center">
              <Lock size={8} />
            </span>
          )}
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-900 dark:text-night-text">{formatWeekRange(currentWeekStart)}</div>
          {isCurrentWeek && (
            <div className="text-[10px] uppercase tracking-wide text-primary-600 dark:text-primary-400 font-semibold">This Week</div>
          )}
        </div>
        <button
          onClick={() => navigateWeek(1)}
          aria-label="Next week"
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text"
        >
          <ChevronRight size={20} />
          {!isPremium && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-amber-500 text-white flex items-center justify-center">
              <Lock size={8} />
            </span>
          )}
        </button>
      </div>

      {allPlannedRecipeIds.length > 0 && (
        <Button onClick={handleGenerateGroceryList} className="w-full">
          <ShoppingCart size={18} aria-hidden="true" />
          Generate Grocery List ({allPlannedRecipeIds.length} recipes)
        </Button>
      )}

      <div className="space-y-3">
        {DAYS.map((day, dayIndex) => (
          <div
            key={day}
            className={[
              'bg-white dark:bg-night-surface border rounded-xl p-3',
              isCurrentWeek && dayIndex === todayIndex
                ? 'border-primary-500 dark:border-primary-400'
                : 'border-gray-100 dark:border-night-card',
            ].join(' ')}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-night-text">{DAY_LABELS[dayIndex]}</span>
              {isCurrentWeek && dayIndex === todayIndex && (
                <span className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-label="Today" />
              )}
            </div>
            <div className="space-y-2">
              {MEALS.map((meal) => {
                const mealRecipes = days[day]?.[meal] || [];
                return (
                  <div key={meal} className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-night-muted w-16">
                      {meal}
                    </span>
                    {mealRecipes.map((rid) => {
                      const recipe = getRecipe(rid);
                      if (!recipe) return null;
                      return (
                        <button
                          key={`${rid}-${meal}`}
                          onClick={() => navigate(`/recipes/${rid}`)}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium max-w-full"
                        >
                          <span className="truncate">{recipe.title}</span>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Remove"
                            onClick={(e) => { e.stopPropagation(); removeRecipeFromDay(day, meal, rid); }}
                            className="p-0.5 rounded hover:bg-primary-100 dark:hover:bg-primary-900/50"
                          >
                            <X size={12} />
                          </span>
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setShowRecipePicker({ day, meal })}
                      aria-label={`Add ${meal} for ${day}`}
                      className="w-7 h-7 rounded-full border border-dashed border-gray-300 dark:border-night-card text-gray-400 dark:text-night-muted hover:border-primary-500 hover:text-primary-500 flex items-center justify-center"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Modal
        isOpen={!!showRecipePicker}
        onClose={() => { setShowRecipePicker(null); setRecipeSearch(''); }}
        title={
          showRecipePicker
            ? `Add to ${showRecipePicker.day}'s ${showRecipePicker.meal}`
            : ''
        }
      >
        <input
          type="text"
          autoFocus
          placeholder="Search recipes…"
          value={recipeSearch}
          onChange={(e) => setRecipeSearch(e.target.value)}
          className="w-full px-3 py-2 mb-3 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="max-h-[55vh] overflow-y-auto space-y-1.5">
          {filteredRecipes.map((recipe) => (
            <button
              key={recipe.id}
              onClick={() => showRecipePicker && addRecipeToDay(showRecipePicker.day, showRecipePicker.meal, recipe.id)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card"
            >
              <div className="text-sm font-medium text-gray-900 dark:text-night-text">{recipe.title}</div>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-night-muted">
                {recipe.totalTime > 0 && (
                  <span className="inline-flex items-center gap-1"><Clock size={12} aria-hidden="true" /> {recipe.totalTime}m</span>
                )}
                {recipe.servings > 0 && (
                  <span className="inline-flex items-center gap-1"><Users size={12} aria-hidden="true" /> {recipe.servings}</span>
                )}
              </div>
            </button>
          ))}
          {filteredRecipes.length === 0 && (
            <p className="text-sm text-center text-gray-500 dark:text-night-muted py-4">No recipes found</p>
          )}
        </div>
      </Modal>
    </div>
  );
}
